import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import moment from "moment";
import PrintHeader from "../General/PrintHeader";
import Page from "../General/Page";
import {
  formatMoney,
  sumLedgerRows,
  computeLedgerTotals,
  getChallanSettlement,
} from "./ledgerCalcs";

/*
 * Printable student fee history.
 *
 * Opened from the Student Ledger screen, carrying whatever filter is applied there
 * (month/year or an exact challan number) so the printout matches what the user is
 * looking at. Everything is computed with the same helpers the screen uses, so the
 * figures cannot disagree.
 *
 * Layout: student details, then the AD ledger lines with totals, then every voucher
 * on file with its settlement position - the whole picture on one report.
 */
function PrintStudentLedger() {
  const params = useParams();
  const studentId = params.id;

  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [fullLedger, setFullLedger] = useState([]);
  const [challans, setChallans] = useState([]);

  const urlParams = new URLSearchParams(window.location.search);
  const filterMonth = urlParams.get("month") || "";
  const filterYear = urlParams.get("year") || "";
  const filterChallanNo = (urlParams.get("challanNo") || "").trim();

  useEffect(() => {
    document.title = "Student Ledger Report";

    const auth = {
      AccessKey,
      UserID,
      MemberType: MemberType || "Admin",
      Student_ID: parseInt(studentId, 10) || 0,
    };

    const post = (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      });

    Promise.all([
      // GetStudentByID takes ID, not Student_ID, and answers on .Student
      post("Student/GetStudentByID", { ...auth, ID: parseInt(studentId, 10) || 0 }).catch(() => null),
      post("ADLedger/GetADLedgerOfStudentAll", auth).catch(() => null),
      post("Student/GetChallansForStudent", auth).catch(() => null),
    ])
      .then(([studentRes, ledgerRes, challanRes]) => {
        if (studentRes && studentRes.data && studentRes.data.status_code === 1) {
          setStudent(studentRes.data.Student || null);
        }
        if (ledgerRes && ledgerRes.data && ledgerRes.data.status_code === 1) {
          setFullLedger(ledgerRes.data.ListofRecords || []);
        }
        if (challanRes && challanRes.data && challanRes.data.status_code === 1) {
          setChallans(challanRes.data.ListofRecords || []);
        }
      })
      .finally(() => setLoading(false));
  }, [AccessKey, UserID, MemberType, studentId]);

  // Same filter rule as the screen: an exact challan number wins, otherwise month+year.
  const filteredLedger = React.useMemo(() => {
    if (filterChallanNo) {
      return fullLedger.filter(
        (item) => (item.Challan_No || "").trim() === filterChallanNo
      );
    }
    if (filterMonth && filterYear) {
      const m = parseInt(filterMonth, 10);
      return fullLedger.filter((item) => {
        if (!item.PostingDate || !moment(item.PostingDate).isValid()) return false;
        return (
          moment(item.PostingDate).month() + 1 === m &&
          moment(item.PostingDate).format("YYYY") === filterYear
        );
      });
    }
    return fullLedger;
  }, [fullLedger, filterMonth, filterYear, filterChallanNo]);

  const filterLabel = filterChallanNo
    ? `Challan ${filterChallanNo}`
    : filterMonth && filterYear
      ? `${moment().month(parseInt(filterMonth, 10) - 1).format("MMMM")} ${filterYear}`
      : "Complete history";

  const totalsFiltered = computeLedgerTotals(filteredLedger);
  const totalsAll = computeLedgerTotals(fullLedger);
  const rawFiltered = sumLedgerRows(filteredLedger);

  const challanTotals = challans.reduce(
    (acc, ch) => {
      const s = getChallanSettlement(ch, fullLedger);
      return {
        due: acc.due + s.due,
        received: acc.received + s.received,
        balance: acc.balance + s.balance,
      };
    },
    { due: 0, received: 0, balance: 0 }
  );

  /*
   * Unpaid vouchers whose debt was never rolled into a later one.
   *
   * Normally an unpaid month reappears as Arrears on the next voucher, which is why
   * net outstanding only counts the latest row per fee head. That breaks when a
   * student has two live vouchers for one month - an installment split, or a
   * regenerated voucher. The carry-forward reads the latest ledger row per fee code;
   * if that row belongs to the settled twin it sees nothing pending and carries no
   * arrears, so the unpaid twin is quietly dropped and net outstanding understates
   * the debt. Surfacing it here rather than letting the report look tidy and wrong.
   */
  const strandedVouchers = React.useMemo(() => {
    const toDate = (ch) =>
      new Date(parseInt(ch.Year, 10) || 0, (parseInt(ch.Month, 10) || 1) - 1, 1).getTime();
    const anyLaterCarriesArrears = (ch) => {
      const t = toDate(ch);
      return challans.some((other) => {
        if (toDate(other) <= t) return false;
        return fullLedger.some(
          (l) =>
            (l.Challan_No || "").trim() === (other.Challan_No || "").trim() &&
            parseFloat(l.Arrears) > 0
        );
      });
    };
    return challans.filter((ch) => {
      const s2 = getChallanSettlement(ch, fullLedger);
      if (s2.balance <= 0) return false;
      const t = toDate(ch);
      const hasLater = challans.some((other) => toDate(other) > t);
      return hasLater && !anyLaterCarriesArrears(ch);
    });
  }, [challans, fullLedger]);

  const strandedTotal = strandedVouchers.reduce(
    (sum, ch) => sum + getChallanSettlement(ch, fullLedger).balance,
    0
  );

  const studentName = student?.Student_Name || "";
  const regNo = student?.Registration_No || "";
  const className = student?.SelectedClass?.Class_Name || "";
  const sectionName = student?.SelectedSection?.Section_Name || "";
  const wingName = student?.SelectedClass?.SelectedWing?.Wing_Name || "";

  const renderLedgerRows = () => {
    if (filteredLedger.length === 0) {
      return (
        <tr>
          <td colSpan="12" className="text-center">
            No ledger entries for this selection
          </td>
        </tr>
      );
    }
    return filteredLedger.map((item, index) => (
      <tr key={item.ID != null ? `l-${item.ID}` : index}>
        <td>{index + 1}</td>
        <td>{item.PostingDate ? moment(item.PostingDate).format("MMM YYYY") : ""}</td>
        <td>{item.Challan_No}</td>
        <td>{item.Reference_ID_Type || "Monthly"}</td>
        <td>{item.FeeCode}</td>
        <td>{item.Description}</td>
        <td className="text-end">{formatMoney(item.Fee_Posted)}</td>
        <td className="text-end">{formatMoney(item.ConcessionAmount)}</td>
        <td className="text-end">{formatMoney(item.Arrears)}</td>
        <td className="text-end">{formatMoney(item.ReceiveableFee)}</td>
        <td className="text-end">{formatMoney(item.ReceivedAmount)}</td>
        <td className="text-end">{formatMoney(item.PendingFee)}</td>
      </tr>
    ));
  };

  const renderChallanRows = () => {
    if (challans.length === 0) {
      return (
        <tr>
          <td colSpan="9" className="text-center">
            No vouchers on file
          </td>
        </tr>
      );
    }
    return challans.map((ch, index) => {
      const s = getChallanSettlement(ch, fullLedger);
      return (
        <tr key={`${ch.Challan_No || "ch"}-${index}`}>
          <td>{index + 1}</td>
          <td>
            {moment().month((ch.Month || 1) - 1).format("MMM")} {ch.Year}
          </td>
          <td>{ch.Challan_No}</td>
          <td>{ch.Dated ? moment(ch.Dated).format("DD/MM/YYYY") : ""}</td>
          <td>{ch.Due_Date ? moment(ch.Due_Date).format("DD/MM/YYYY") : ""}</td>
          <td className="text-end">{formatMoney(s.due)}</td>
          <td className="text-end">{formatMoney(s.received)}</td>
          <td className="text-end">{formatMoney(s.balance)}</td>
          <td>
            {s.status}
            {ch.Received_Date ? ` (${moment(ch.Received_Date).format("DD/MM/YYYY")})` : ""}
          </td>
        </tr>
      );
    });
  };

  if (loading) {
    return (
      <div className="ledger-report">
        <Page orientation="landscape" size="a4">
          <div className="text-center p-5">
            <Spinner animation="border" role="status" />
          </div>
        </Page>
      </div>
    );
  }

  return (
    <div className="ledger-report">
      {/* Scoped to this report. The shared .table-print rule blanks the outer borders
          and .page .content forces a portrait height, neither of which suits a wide
          landscape statement. @page is what actually makes the browser print A4
          landscape - the .page class only sizes the on-screen sheet. */}
      <style>{`
        @page { size: A4 landscape; margin: 8mm; }

        .ledger-report .page.a4.landscape { width: 297mm; min-height: 200mm; box-shadow: none; }
        .ledger-report .page .content { min-height: auto; padding: 0; }

        .ledger-report table.table-print { border-collapse: collapse; width: 100%; margin-bottom: 8px; }
        .ledger-report table.table-print th,
        .ledger-report table.table-print td {
          border: 1px solid #000 !important;
          padding: 2px 4px !important;
          font-size: 10px !important;
          vertical-align: middle;
          color: #000;
        }
        .ledger-report table.table-print thead th { background: #e9e9e9; font-weight: 700; }
        .ledger-report table.table-print tbody th { background: #f6f6f6; font-weight: 700; }
        .ledger-report .text-end { text-align: right; }
        .ledger-report h5 { margin: 10px 0 4px; }
        .ledger-report p { margin: 0 0 8px; }

        @media print {
          html, body { background: #fff; margin: 0; padding: 0; }
          .ledger-report .page { box-shadow: none; margin: 0; width: auto; min-height: 0; }
          .ledger-report table.table-print { page-break-inside: auto; }
          .ledger-report table.table-print tr { page-break-inside: avoid; }
          .ledger-report table.table-print thead { display: table-header-group; }
        }
      `}</style>
    <Page orientation="landscape" size="a4">
      <PrintHeader title={`Student Fee Ledger — ${filterLabel}`} />

      {/* ---------- Student ---------- */}
      <table className="table table-sm table-bordered table-print mb-3">
        <tbody>
          <tr>
            <th style={{ width: "12%" }}>Registration No</th>
            <td style={{ width: "21%" }}>{regNo}</td>
            <th style={{ width: "12%" }}>Student Name</th>
            <td style={{ width: "22%" }}>{studentName}</td>
            <th style={{ width: "12%" }}>Father Name</th>
            <td>{student?.Father_Name || ""}</td>
          </tr>
          <tr>
            <th>Class / Section</th>
            <td>
              {className}
              {sectionName ? ` (${sectionName})` : ""}
              {wingName ? ` — ${wingName}` : ""}
            </td>
            <th>Contact</th>
            <td>{student?.Contact_Mobile || student?.Contact_Phone || ""}</td>
            <th>Report Scope</th>
            <td>{filterLabel}</td>
          </tr>
        </tbody>
      </table>

      {/* ---------- Position ---------- */}
      <table className="table table-sm table-bordered table-print mb-3">
        <thead>
          <tr>
            <th colSpan="4">Overall Position (complete history, all fee heads)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th style={{ width: "25%" }}>Total Received (lifetime)</th>
            <td style={{ width: "25%" }} className="text-end">
              {formatMoney(totalsAll.totalReceived)}
            </td>
            <th style={{ width: "25%" }}>Net Outstanding</th>
            <td className="text-end">
              <b>{formatMoney(totalsAll.outstanding)}</b>
            </td>
          </tr>
        </tbody>
      </table>
      <p style={{ fontSize: "8pt", marginTop: "-8px" }}>
        Net outstanding counts the latest ledger row per fee head. Unpaid amounts are
        carried into the next month as Arrears, so the same debt appears on several rows
        and adding every row would overstate what is owed.
      </p>
      {strandedVouchers.length > 0 ? (
        <table className="table table-sm table-bordered table-print mb-3">
          <thead>
            <tr>
              <th colSpan="3" style={{ background: "#f2f2f2" }}>
                Unpaid vouchers NOT included in Net Outstanding — needs review
              </th>
            </tr>
            <tr>
              <th>Voucher</th>
              <th>Month</th>
              <th className="text-end">Unpaid Balance</th>
            </tr>
          </thead>
          <tbody>
            {strandedVouchers.map((ch, i) => {
              const s3 = getChallanSettlement(ch, fullLedger);
              return (
                <tr key={`st-${ch.Challan_No || i}`}>
                  <td>{ch.Challan_No}</td>
                  <td>
                    {moment().month((ch.Month || 1) - 1).format("MMM")} {ch.Year}
                  </td>
                  <td className="text-end">{formatMoney(s3.balance)}</td>
                </tr>
              );
            })}
            <tr>
              <th colSpan="2" className="text-end">
                Total not carried forward
              </th>
              <th className="text-end">{formatMoney(strandedTotal)}</th>
            </tr>
          </tbody>
        </table>
      ) : null}
      {strandedVouchers.length > 0 ? (
        <p style={{ fontSize: "8pt", marginTop: "-8px" }}>
          These vouchers are unpaid but their amounts were never carried into a later
          voucher as arrears, so Net Outstanding above does not include them. It happens
          where a month has two live vouchers (an installment split, or a regenerated
          voucher) and the carry-forward followed the settled one. True amount owed is{" "}
          <b>{formatMoney(totalsAll.outstanding + strandedTotal)}</b>.
        </p>
      ) : null}

      {/* ---------- AD Ledger ---------- */}
      <h5 style={{ fontSize: "11pt", fontWeight: 600, marginTop: "12px" }}>
        AD Ledger Entries — {filterLabel}
      </h5>
      <table className="table table-sm table-bordered table-print">
        <thead>
          <tr>
            <th>Sr.#</th>
            <th>Fee Month</th>
            <th>Challan No</th>
            <th>Type</th>
            <th>Code</th>
            <th>Description</th>
            <th className="text-end">Fee Posted</th>
            <th className="text-end">Concession</th>
            <th className="text-end">Arrears</th>
            <th className="text-end">Receivable</th>
            <th className="text-end">Received</th>
            <th className="text-end">Pending</th>
          </tr>
        </thead>
        <tbody>
          {renderLedgerRows()}
          {filteredLedger.length > 0 ? (
            <>
              <tr>
                <th colSpan="6" className="text-end">
                  Sum of listed rows
                </th>
                <th className="text-end">{formatMoney(rawFiltered.feePosted)}</th>
                <th className="text-end">{formatMoney(rawFiltered.concession)}</th>
                <th className="text-end">{formatMoney(rawFiltered.arrears)}</th>
                <th className="text-end">{formatMoney(rawFiltered.receiveable)}</th>
                <th className="text-end">{formatMoney(rawFiltered.received)}</th>
                <th className="text-end">{formatMoney(rawFiltered.pending)}</th>
              </tr>
              <tr>
                <th colSpan="9" className="text-end">
                  Net for this selection — outstanding counts the latest row per fee head
                </th>
                <th className="text-end">—</th>
                <th className="text-end">{formatMoney(totalsFiltered.totalReceived)}</th>
                <th className="text-end">{formatMoney(totalsFiltered.outstanding)}</th>
              </tr>
            </>
          ) : null}
        </tbody>
      </table>

      {/* ---------- Challans ---------- */}
      <h5 style={{ fontSize: "11pt", fontWeight: 600, marginTop: "12px" }}>
        Fee Vouchers (Challan View) — complete history
      </h5>
      <table className="table table-sm table-bordered table-print">
        <thead>
          <tr>
            <th>Sr.#</th>
            <th>Month</th>
            <th>Challan No</th>
            <th>Dated</th>
            <th>Due Date</th>
            <th className="text-end">Due Amount</th>
            <th className="text-end">Received</th>
            <th className="text-end">Balance</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {renderChallanRows()}
          {challans.length > 0 ? (
            <tr>
              <th colSpan="5" className="text-end">
                Totals
              </th>
              <th className="text-end">{formatMoney(challanTotals.due)}</th>
              <th className="text-end">{formatMoney(challanTotals.received)}</th>
              <th className="text-end">{formatMoney(challanTotals.balance)}</th>
              <th></th>
            </tr>
          ) : null}
        </tbody>
      </table>
      <p style={{ fontSize: "8pt" }}>
        Voucher due amounts include arrears carried forward from earlier months, so the
        column total is not the amount owed — use Net Outstanding above for that. Received
        and Status are taken from the ledger lines, so part payments are shown.
        Only live vouchers appear; a voucher replaced by a regenerated one is not listed.
      </p>
    </Page>
    </div>
  );
}

export default PrintStudentLedger;
